import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React from "react";

export interface InternTableColumn<T> {
  id: string;
  label: string;
  width?: number | string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  render: (row: T, index: number) => React.ReactNode;
}

interface InternTableProps<T> {
  columns: InternTableColumn<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  onRowClick?: (row: T) => void;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (field: string) => void;
  emptyMessage?: string;
  stickyHeader?: boolean;
  maxHeight?: number | string;
  variant?: "default" | "compact";
  /** Drop outer border/radius when nested inside another surface */
  nested?: boolean;
}

function InternTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  sortField,
  sortDirection,
  onSort,
  emptyMessage = "No data available",
  stickyHeader = false,
  maxHeight,
  variant = "default",
  nested = false,
}: InternTableProps<T>) {
  const isCompact = variant === "compact";

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: nested ? "none" : "1px solid var(--intern-border-default)",
        borderRadius: nested ? 0 : 2,
        maxHeight: maxHeight,
        height: nested && maxHeight ? "100%" : undefined,
        "& .MuiTableCell-root": {
          borderColor: "var(--intern-border-default)",
        },
      }}
    >
      <Table size="small" stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || "left"}
                sx={{
                  bgcolor: "var(--intern-bg-secondary)",
                  fontWeight: 600,
                  fontSize: isCompact ? "0.75rem" : "0.8125rem",
                  py: isCompact ? 1 : 1.25,
                  px: isCompact ? 1.5 : 2,
                  width: column.width,
                  color: "var(--intern-text-secondary)",
                }}
              >
                {column.sortable && onSort ? (
                  <TableSortLabel
                    active={sortField === column.id}
                    direction={sortField === column.id ? sortDirection : "asc"}
                    onClick={() => onSort(column.id)}
                    sx={{
                      "&.Mui-active": {
                        color: "var(--intern-text-primary)",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "var(--intern-text-tertiary) !important",
                      },
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={keyExtractor(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? "pointer" : "default",
                "&:hover": onRowClick
                  ? {
                      bgcolor: "var(--intern-bg-hover)",
                    }
                  : {},
                "&:last-child td": {
                  borderBottom: 0,
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  sx={{
                    py: isCompact ? 0.75 : 1,
                    px: isCompact ? 1.5 : 2,
                    fontSize: isCompact ? "0.75rem" : "0.8125rem",
                    color: "var(--intern-text-primary)",
                  }}
                >
                  {column.render(row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {/* Empty state */}
          {data.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{
                  py: 6,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "var(--intern-text-tertiary)" }}
                >
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default InternTable;
